import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import {
  CatalogNavigationDto,
  CatalogNavigationModel,
  DynamicFilterModel,
  DynamicFilterOptionModel,
  SelectedDynamicFilterModel,
  DtoState,
  FilterState,
  PriceRange,
  CatalogItemDto,
  SearchSessionState,
  FacetedCategoryDto,
  ItemReplicaDto,
} from '@marketplace-web/catalog/catalog-data'
import { BrandModel } from '@marketplace-web/personalization/brands-data'
import { PaginationDto } from '@marketplace-web/shared/pagination-data'

type ActionType<T extends string, P> = { type: T; payload: P }

export enum FilterReducerAction {
  CHANGE_FILTERS = 'CHANGE_FILTERS',
  SET_INITIAL_FILTERS = 'SET_INITIAL_FILTERS',
}

export type ChangeFiltersAction = ActionType<
  FilterReducerAction.CHANGE_FILTERS,
  { filters: Partial<FilterState> }
>

export type SetInitialFiltersAction = ActionType<
  FilterReducerAction.SET_INITIAL_FILTERS,
  { filters: Partial<FilterState> }
>

export type FilterAction = ChangeFiltersAction | SetInitialFiltersAction

export const isFilterAction = (action: CatalogAction): action is FilterAction => {
  return Object.values(FilterReducerAction).includes(action.type as FilterReducerAction)
}
export enum DynamicFilterReducerAction {
  FETCH_DYNAMIC_FILTERS_REQUEST = 'FETCH_DYNAMIC_FILTERS_REQUEST',
  FETCH_DYNAMIC_FILTERS_SUCCESS = 'FETCH_DYNAMIC_FILTERS_SUCCESS',
  FETCH_DYNAMIC_FILTERS_FAILURE = 'FETCH_DYNAMIC_FILTERS_FAILURE',
  FETCH_TEMPORARY_DYNAMIC_FILTERS_SUCCESS = 'FETCH_TEMPORARY_DYNAMIC_FILTERS_SUCCESS',
  FETCH_DYNAMIC_FILTER_SEARCH_REQUEST = 'FETCH_DYNAMIC_FILTER_SEARCH_REQUEST',
  FETCH_DYNAMIC_FILTER_SEARCH_SUCCESS = 'FETCH_DYNAMIC_FILTER_SEARCH_SUCCESS',
  FETCH_DYNAMIC_FILTER_SEARCH_FAILURE = 'FETCH_DYNAMIC_FILTER_SEARCH_FAILURE',
  FETCH_DYNAMIC_FILTER_FACETS_REQUEST = 'FETCH_DYNAMIC_FILTER_FACETS_REQUEST',
  FETCH_DYNAMIC_FILTER_FACETS_SUCCESS = 'FETCH_DYNAMIC_FILTER_FACETS_SUCCESS',
  FETCH_DYNAMIC_FILTER_FACETS_FAILURE = 'FETCH_DYNAMIC_FILTER_FACETS_FAILURE',
  SET_SELECTED_DYNAMIC_FILTERS = 'SET_SELECTED_DYNAMIC_FILTERS',
  SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS = 'SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS',
  SET_TEMPORARY_CATALOG_IDS = 'SET_TEMPORARY_CATALOG_IDS',
  UPDATE_TEMPORARY_CATALOG_IDS = 'UPDATE_TEMPORARY_CATALOG_IDS',
  SET_TEMPORARY_PRICE_RANGE = 'SET_TEMPORARY_PRICE_RANGE',
  SET_FILTER_SEARCH_QUERY = 'SET_FILTER_SEARCH_QUERY',
  SET_DYNAMIC_FILTER_FACETS_COUNTS = 'SET_DYNAMIC_FILTER_FACETS_COUNTS',
  SET_DYNAMIC_FILTER_OPTIONS = 'SET_DYNAMIC_FILTER_OPTIONS',
}

export type FetchDynamicFiltersRequestAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_REQUEST,
  undefined
>

export type FetchDynamicFiltersSuccessAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_SUCCESS,
  {
    dynamicFilters: Array<DynamicFilterModel>
    selectedDynamicFilters: Array<SelectedDynamicFilterModel>
  }
>

export type FetchDynamicFiltersFailureAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_FAILURE,
  {
    errors: Array<ErrorItem>
  }
>

export type FetchTemporaryDynamicFiltersSuccessAction = ActionType<
  DynamicFilterReducerAction.FETCH_TEMPORARY_DYNAMIC_FILTERS_SUCCESS,
  {
    dynamicFilters: Array<DynamicFilterModel>
    temporarySelectedDynamicFilters: Array<SelectedDynamicFilterModel>
  }
>

export type FetchDynamicFilterSearchRequestAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTER_SEARCH_REQUEST,
  undefined
>

export type FetchDynamicFilterSearchSuccessAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTER_SEARCH_SUCCESS,
  {
    code: string
    options: Array<DynamicFilterOptionModel>
    isSelectionHighlighted?: boolean
  }
>

export type FetchDynamicFilterSearchFailureAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTER_SEARCH_FAILURE,
  {
    errors: Array<ErrorItem>
  }
>

export type SetSelectedDynamicFiltersAction = ActionType<
  DynamicFilterReducerAction.SET_SELECTED_DYNAMIC_FILTERS,
  {
    selectedDynamicFilters: Array<SelectedDynamicFilterModel>
  }
>

export type SetTemporarySelectedDynamicFiltersAction = ActionType<
  DynamicFilterReducerAction.SET_TEMPORARY_SELECTED_DYNAMIC_FILTERS,
  {
    temporarySelectedDynamicFilters: Array<SelectedDynamicFilterModel>
  }
>

export type SetTemporaryCatalogIdsAction = ActionType<
  DynamicFilterReducerAction.SET_TEMPORARY_CATALOG_IDS,
  {
    temporaryCatalogIds: Array<number>
  }
>

export type UpdateTemporaryCatalogIdsAction = ActionType<
  DynamicFilterReducerAction.UPDATE_TEMPORARY_CATALOG_IDS,
  {
    temporaryCatalogIds: Array<number>
  }
>

export type SetTemporaryPriceRangeAction = ActionType<
  DynamicFilterReducerAction.SET_TEMPORARY_PRICE_RANGE,
  {
    priceRange: PriceRange
  }
>

export type SetFilterSearchQueryAction = ActionType<
  DynamicFilterReducerAction.SET_FILTER_SEARCH_QUERY,
  {
    text: string | null
    code: string | null
  }
>

export type FetchDynamicFilterFacetsRequestAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTER_FACETS_REQUEST,
  undefined
>

export type FetchDynamicFilterFacetsSuccessAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTER_FACETS_SUCCESS,
  {
    totalCount: number | null
    totalCountMax: number | null
    code: string | null
    options: Array<DynamicFilterOptionModel>
  }
>

export type FetchDynamicFilterFacetsFailureAction = ActionType<
  DynamicFilterReducerAction.FETCH_DYNAMIC_FILTER_FACETS_FAILURE,
  {
    errors: Array<ErrorItem>
  }
>

export type SetDynamicFilterFacetsAction = ActionType<
  DynamicFilterReducerAction.SET_DYNAMIC_FILTER_FACETS_COUNTS,
  {
    filterFacetsCount: number | null
  }
>

export type SetDynamicFilterOptionsAction = ActionType<
  DynamicFilterReducerAction.SET_DYNAMIC_FILTER_OPTIONS,
  {
    code: string | null
    newOptions: Array<DynamicFilterOptionModel>
  }
>

export type DynamicFilterAction =
  | FetchDynamicFiltersRequestAction
  | FetchDynamicFiltersSuccessAction
  | FetchDynamicFiltersFailureAction
  | FetchTemporaryDynamicFiltersSuccessAction
  | FetchDynamicFilterSearchRequestAction
  | FetchDynamicFilterSearchSuccessAction
  | FetchDynamicFilterSearchFailureAction
  | FetchDynamicFilterFacetsRequestAction
  | FetchDynamicFilterFacetsSuccessAction
  | FetchDynamicFilterFacetsFailureAction
  | SetSelectedDynamicFiltersAction
  | SetTemporarySelectedDynamicFiltersAction
  | SetTemporaryCatalogIdsAction
  | SetTemporaryPriceRangeAction
  | UpdateTemporaryCatalogIdsAction
  | SetFilterSearchQueryAction
  | SetDynamicFilterFacetsAction
  | SetDynamicFilterOptionsAction

export const isDynamicFilterAction = (action: CatalogAction): action is DynamicFilterAction => {
  return Object.values(DynamicFilterReducerAction).includes(
    action.type as DynamicFilterReducerAction,
  )
}

export enum DtoReducerAction {
  CHANGE_DTO = 'CHANGE_DTO',
}

export type DtoAction = ActionType<
  DtoReducerAction.CHANGE_DTO,
  {
    dtos: DtoState
  }
>

export const isDtoAction = (action: CatalogAction): action is DtoAction => {
  return Object.values(DtoReducerAction).includes(action.type as DtoReducerAction)
}

export enum DataReducerAction {
  FETCH_DATA_REQUEST = 'FETCH_DATA_REQUEST',
  FETCH_DATA_SUCCESS = 'FETCH_DATA_SUCCESS',
  FETCH_DATA_FAILURE = 'FETCH_DATA_FAILURE',
}

export type FetchDataRequestAction = ActionType<
  DataReducerAction.FETCH_DATA_REQUEST,
  {
    relativeUrl: string
    searchParams: Record<string, string | Array<string> | undefined>
  }
>

export type FetchDataSuccessAction = ActionType<
  DataReducerAction.FETCH_DATA_SUCCESS,
  {
    catalogTree: Array<CatalogNavigationDto>
    catalogs: Array<CatalogNavigationModel>
  }
>

export type FetchDataFailureAction = ActionType<DataReducerAction.FETCH_DATA_FAILURE, undefined>

export type DataAction = FetchDataRequestAction | FetchDataSuccessAction | FetchDataFailureAction

export const isDataAction = (action: CatalogAction): action is DataAction => {
  return Object.values(DataReducerAction).includes(action.type as DataReducerAction)
}

export enum CatalogBrandReducerAction {
  SET_CATALOG_BRAND = 'SET_CATALOG_BRAND',
  REMOVE_CATALOG_BRAND = 'REMOVE_CATALOG_BRAND',
}

export type SetCatalogBrandAction = ActionType<
  CatalogBrandReducerAction.SET_CATALOG_BRAND,
  {
    brand?: BrandModel
  }
>

export type RemoveCatalogBrandAction = ActionType<
  CatalogBrandReducerAction.REMOVE_CATALOG_BRAND,
  undefined
>

export type CatalogBrandAction = SetCatalogBrandAction | RemoveCatalogBrandAction

export const isCatalogBrandAction = (action: CatalogAction): action is CatalogBrandAction => {
  return Object.values(CatalogBrandReducerAction).includes(action.type as CatalogBrandReducerAction)
}

export enum GeneralReducerAction {
  SET_CATALOG_TITLE = 'SET_CATALOG_TITLE',
  INCREMENT_FILTERS_CHANGE_COUNT = 'INCREMENT_FILTERS_CHANGE_COUNT',
}

export type SetCatalogTitleAction = ActionType<
  GeneralReducerAction.SET_CATALOG_TITLE,
  {
    title?: string
  }
>

export type IncrementFiltersChangeCountAction = ActionType<
  GeneralReducerAction.INCREMENT_FILTERS_CHANGE_COUNT,
  undefined
>

export type GeneralAction = SetCatalogTitleAction | IncrementFiltersChangeCountAction

export const isGeneralAction = (action: CatalogAction): action is GeneralAction => {
  return Object.values(GeneralReducerAction).includes(action.type as GeneralReducerAction)
}

export enum ItemsReducerAction {
  FETCH_ITEMS_REQUEST = 'FETCH_ITEMS_REQUEST',
  FETCH_ITEMS_SUCCESS = 'FETCH_ITEMS_SUCCESS',
  FETCH_ITEMS_FAILURE = 'FETCH_ITEMS_FAILURE',
}

export type FetchItemsRequestAction = ActionType<ItemsReducerAction.FETCH_ITEMS_REQUEST, undefined>

export type FetchItemsSuccessAction = ActionType<
  ItemsReducerAction.FETCH_ITEMS_SUCCESS,
  {
    items: Array<CatalogItemDto> | undefined
    searchTrackingParams: SearchSessionState
    pagination: PaginationDto | undefined
    replicaWarning?: ItemReplicaDto
  }
>

export type FetchItemsFailureAction = ActionType<
  ItemsReducerAction.FETCH_ITEMS_FAILURE,
  {
    error: string | undefined
  }
>

export type ItemsAction =
  | FetchItemsRequestAction
  | FetchItemsSuccessAction
  | FetchItemsFailureAction

export const isItemsAction = (action: CatalogAction): action is ItemsAction => {
  return Object.values(ItemsReducerAction).includes(action.type as ItemsReducerAction)
}

export enum FacetedCategoriesReducerAction {
  FETCH_FACETED_CATEGORIES_REQUEST = 'FETCH_FACETED_CATEGORIES_REQUEST',
  FETCH_FACETED_CATEGORIES_SUCCESS = 'FETCH_FACETED_CATEGORIES_SUCCESS',
  FETCH_FACETED_CATEGORIES_FAILURE = 'FETCH_FACETED_CATEGORIES_FAILURE',
}

export type FetchFacetedCategoriesRequestAction = ActionType<
  FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_REQUEST,
  undefined
>

export type FetchFacetedCategoriesSuccessAction = ActionType<
  FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_SUCCESS,
  {
    categories: Array<FacetedCategoryDto> | undefined
  }
>

export type FetchFacetedCategoriesFailureAction = ActionType<
  FacetedCategoriesReducerAction.FETCH_FACETED_CATEGORIES_FAILURE,
  {
    error: string | undefined
  }
>

export type FacetedCategoriesAction =
  | FetchFacetedCategoriesRequestAction
  | FetchFacetedCategoriesSuccessAction
  | FetchFacetedCategoriesFailureAction

export const isFacetedCategoriesAction = (
  action: CatalogAction,
): action is FacetedCategoriesAction => {
  return Object.values(FacetedCategoriesReducerAction).includes(
    action.type as FacetedCategoriesReducerAction,
  )
}

export type CatalogAction =
  | FilterAction
  | DynamicFilterAction
  | DtoAction
  | DataAction
  | CatalogBrandAction
  | GeneralAction
  | ItemsAction
  | FacetedCategoriesAction
